<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { useAddToDashboard } from '../composables/useAddToDashboard'
import { useDashboards } from '../composables/useDashboards'
import { SaveDashboardFlow } from '../flows/SaveDashboardFlow'
import type { AddToDashboardTarget } from '../models/addToDashboard.types'
import type { DashboardDefinitionDto } from '../models/api.types'
import type { DashboardWidgetWire } from '../models/widget.types'
import { parseLayout } from '../service/layout/serialize'

import { useAccount, useWorkspace } from '@/features/account'
import { resolvePartitionColumns, useLevelMetadata } from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import {
  Alert,
  Button,
  Input,
  Modal,
  RadioGroup,
  Select,
  type RadioOption,
  type SelectOption,
  useToast,
} from '@/shared/ui'

const emit = defineEmits<{ limitReached: [] }>()
const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const account = useAccount()
const workspace = useWorkspace()
const levels = useLevelMetadata()
const bridge = useAddToDashboard()
const dashboards = useDashboards()

const mode = ref<'existing' | 'new'>('existing')
const selectedId = ref<number | null>(null)
const newName = ref('')
const saving = ref(false)
const loadingDefinitions = ref(false)

const open = computed({
  get: () => bridge.isOpen.value,
  set: (value: boolean) => {
    if (!value) bridge.close()
  },
})
const definitions = computed(() => dashboards.workspaceDefinitions.value)
const dashboardOptions = computed<SelectOption<number>[]>(() =>
  definitions.value.map((entry) => ({
    value: entry.id,
    label: entry.name?.trim() || t('dashboards.page.untitled'),
  })),
)
const modeOptions = computed<RadioOption[]>(() => [
  {
    value: 'existing',
    label: t('dashboards.addTo.existing'),
    disabled: dashboardOptions.value.length === 0,
  },
  { value: 'new', label: t('dashboards.addTo.new') },
])
const canSave = computed(
  () =>
    !saving.value &&
    (mode.value === 'existing' ? selectedId.value !== null : newName.value.trim().length > 0),
)

watch(
  () => bridge.isOpen.value,
  async (isOpen) => {
    if (!isOpen) return
    newName.value = bridge.payload.value?.title?.trim() ?? ''
    if (
      definitions.value.length === 0 ||
      definitions.value.some((entry) => !Array.isArray(entry.sections))
    ) {
      loadingDefinitions.value = true
      try {
        await dashboards.loadDefinitions(true)
      } catch {
        toast.error(t('dashboards.addTo.loadFailed'))
      } finally {
        loadingDefinitions.value = false
      }
    }
    selectedId.value = definitions.value[0]?.id ?? null
    mode.value = selectedId.value === null ? 'new' : 'existing'
  },
)

function stringFields(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : []
}

/** Module 08 remains the authority for level and column availability. */
function canResolve(widget: DashboardWidgetWire): boolean {
  if (typeof widget.partition !== 'string' || widget.partition.length === 0) return true
  const level = levels.levelByPartition(widget.partition, widget.accountWorkspaces)
  if (!level) return false

  const dimensions = [
    ...stringFields(widget.setDimensions),
    ...stringFields(widget.chartDimensions),
  ]
  const metrics = [...stringFields(widget.setMetrics), ...stringFields(widget.chartMetrics)]
  const columns = [
    ...level.tableDimensions.map((column) => column.field),
    ...level.tableMetrics.map((column) => column.field),
    ...levels.runtimeMetricFields(widget.partition),
  ]
  const resolved = resolvePartitionColumns({
    tabs: [{ partition_name: widget.partition, columns }],
    dimensions,
    metrics,
    partitionName: widget.partition,
    ...(widget.accountWorkspaces?.length ? { workspaceIds: widget.accountWorkspaces } : {}),
    isAccountCenter: (widget.accountWorkspaces?.length ?? 0) > 0,
  })
  const disabled = new Set(resolved.disabledFields)
  return [...dimensions, ...metrics].every(
    (field) => resolved.fields.includes(field) && !disabled.has(field),
  )
}

function existingTarget(definition: DashboardDefinitionDto): AddToDashboardTarget | null {
  if (!Array.isArray(definition.sections)) return null
  return {
    id: definition.id,
    version: definition.version,
    name: definition.name ?? '',
    ownerId: definition.workspace_id,
    accountId: account.accountId.value ?? null,
    currentUrlId: definition.url_id,
    rows: parseLayout(definition.sections),
  }
}

function newTarget(): AddToDashboardTarget | null {
  const ownerId = workspace.workspaceId.value
  if (ownerId == null) return null
  return {
    name: newName.value,
    ownerId,
    accountId: account.accountId.value ?? null,
    rows: [],
  }
}

async function save(): Promise<void> {
  const payload = bridge.payload.value
  const selected = definitions.value.find((entry) => entry.id === selectedId.value)
  const target = mode.value === 'existing' && selected ? existingTarget(selected) : newTarget()
  if (!payload) return
  if (!target) {
    toast.error(t('dashboards.addTo.loadFailed'))
    return
  }

  saving.value = true
  try {
    const outcome = await new SaveDashboardFlow({
      navigate: async (route) => {
        await router.push(route)
      },
      onLimitReached: () => emit('limitReached'),
    }).append({ target, widgets: payload.widgets, canResolve })

    if (outcome.status === 'saved') {
      bridge.close()
      if (outcome.skipped > 0) {
        toast.warning(t('dashboards.addTo.partialWarning', { count: outcome.skipped }))
      } else {
        toast.success(t('dashboards.addTo.saved'))
      }
      return
    }
    const tone = outcome.status === 'conflict' ? 'warning' : 'error'
    toast[tone](t(outcome.messageKey))
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <Modal
    v-model="open"
    size="md"
    :title="t('dashboards.addTo.title')"
    :close-label="t('dashboards.addTo.close')"
  >
    <template #body>
      <div class="add-to-dashboard grid gap-lg">
        <RadioGroup
          v-model="mode"
          :label="t('dashboards.addTo.selectPrompt')"
          :options="modeOptions"
        />
        <Select
          v-if="mode === 'existing'"
          v-model="selectedId"
          :options="dashboardOptions"
          :label="t('dashboards.addTo.dashboard')"
          :placeholder="t('dashboards.addTo.selectPlaceholder')"
          :state="loadingDefinitions ? 'loading' : dashboardOptions.length ? 'ready' : 'empty'"
          searchable
        />
        <Input
          v-else
          v-model="newName"
          :label="t('dashboards.addTo.newName')"
          :placeholder="t('dashboards.addTo.newNamePlaceholder')"
          @enter="save"
        />
        <Alert v-if="mode === 'existing' && dashboardOptions.length === 0" tone="info">
          {{ t('dashboards.addTo.noDashboards') }}
        </Alert>
      </div>
    </template>
    <template #buttons>
      <Button variant="ghost" :disabled="saving" @click="bridge.close()">
        {{ t('dashboards.addTo.cancel') }}
      </Button>
      <Button
        data-testid="add-to-dashboard-save"
        :loading="saving"
        :disabled="!canSave"
        @click="save"
      >
        {{ t('dashboards.addTo.save') }}
      </Button>
    </template>
  </Modal>
</template>
